import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import baseUrl from "../../../api/baseUrl";

/**
 * Resolves a provisional BookMyDentist patient (regNo "BMD-<n>", created
 * automatically when someone books online — phone unverified):
 * - Link to an existing patient: moves the booking's appointment, treatment
 *   sheet and invoice onto that patient in one server action and removes the
 *   provisional record. Invoice numbers and amounts never change.
 * - Register as new: issues a real regNo now (clinic's numbering, in sequence)
 *   and the record becomes the permanent patient.
 */

type ProvisionalPatient = {
  _id: string;
  fullName: string;
  regNo: string;
  mobileNo?: string;
};

type CandidatePatient = {
  _id: string;
  fullName: string;
  title?: string;
  regNo: string;
  mobileNo?: string;
  provisional?: boolean;
};

interface Props {
  patient: ProvisionalPatient;
  onClose: () => void;
  /** Called after a successful resolve so the caller can refresh the list. */
  onResolved: () => void;
}

const ResolveProvisionalModal: React.FC<Props> = ({ patient, onClose, onResolved }) => {
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [keyword, setKeyword] = useState("");
  const [candidates, setCandidates] = useState<CandidatePatient[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (mode !== "existing") return;
    const term = keyword.trim();
    if (!term) {
      setCandidates([]);
      return;
    }
    const timer = window.setTimeout(() => {
      const controller = new AbortController();
      if (requestRef.current) requestRef.current.abort();
      requestRef.current = controller;
      setSearching(true);
      baseUrl
        .get("client/patient/find-all-patients", {
          headers: { status: true, page: 1, keyword: term, searchBy: "all" },
          signal: controller.signal,
        })
        .then((response) => {
          if (requestRef.current !== controller) return;
          const rows: CandidatePatient[] = Array.isArray(response.data?.data)
            ? response.data.data
            : [];
          // Provisional records can't be swap targets.
          setCandidates(
            rows.filter((p) => !p.provisional && String(p._id) !== String(patient._id))
          );
        })
        .catch(() => {
          if (requestRef.current !== controller) return;
          setCandidates([]);
        })
        .finally(() => {
          if (requestRef.current === controller) setSearching(false);
        });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [keyword, mode, patient._id]);

  const handleConfirm = () => {
    if (saving) return;
    setSaving(true);

    if (mode === "existing") {
      if (!selectedId) {
        toast.error("Select the patient to link this booking to.");
        setSaving(false);
        return;
      }
      baseUrl
        .patch("client/patient/reassign-provisional", {
          provisionalPatientId: patient._id,
          targetPatientId: selectedId,
        })
        .then((res) => {
          if (res.data?.success) {
            toast.success("Booking records moved to the selected patient.");
            onResolved();
            onClose();
          } else {
            toast.error(res.data?.message || "Failed to reassign booking records");
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message || "Failed to reassign booking records");
        })
        .finally(() => setSaving(false));
      return;
    }

    baseUrl
      .patch("client/patient/confirm-provisional", { patientId: patient._id })
      .then((res) => {
        if (res.data?.success) {
          toast.success(
            `Patient registered — reg no ${res.data?.regNo ?? ""}. Complete their details via Edit.`
          );
          onResolved();
          onClose();
        } else {
          toast.error(res.data?.message || "Failed to register patient");
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || "Failed to register patient");
      })
      .finally(() => setSaving(false));
  };

  const optionClass = (active: boolean) =>
    `flex-1 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
      active
        ? "border-indigo-400 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-200"
        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
    }`;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4">
      <div
        className="w-full max-w-[520px] rounded-xl bg-white p-4 shadow-2xl dark:bg-slate-800 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-0">
            Resolve online booking patient
          </h3>
          <button
            type="button"
            className="rounded-full p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700"
            onClick={onClose}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
          <span className="font-semibold">{patient.fullName}</span> · {patient.regNo}
          {patient.mobileNo ? ` · ${patient.mobileNo}` : ""} — booked via BookMyDentist. Their
          appointment, consultation line and invoice will follow whichever option you pick.
        </div>

        <div className="mb-4 flex gap-2">
          <button type="button" className={optionClass(mode === "existing")} onClick={() => setMode("existing")}>
            <span className="flex items-center gap-1.5 font-semibold">
              <SwapHorizIcon sx={{ fontSize: 16 }} /> Link to existing patient
            </span>
            <span className="mt-0.5 block text-[11px] opacity-80">
              They already have a record — move the booking onto it.
            </span>
          </button>
          <button type="button" className={optionClass(mode === "new")} onClick={() => setMode("new")}>
            <span className="flex items-center gap-1.5 font-semibold">
              <PersonAddIcon sx={{ fontSize: 16 }} /> Register as new
            </span>
            <span className="mt-0.5 block text-[11px] opacity-80">
              First visit — issue a real reg no now.
            </span>
          </button>
        </div>

        {mode === "existing" ? (
          <div className="mb-4">
            <div className="relative">
              <SearchIcon
                sx={{ fontSize: 16 }}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setSelectedId(null);
                }}
                placeholder="Search by name, reg no or phone…"
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              />
            </div>
            <div className="mt-2 max-h-[220px] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-600">
              {searching ? (
                <p className="px-3 py-3 text-xs text-slate-500">Searching…</p>
              ) : candidates.length === 0 ? (
                <p className="px-3 py-3 text-xs text-slate-500">
                  {keyword.trim() ? "No matching patients." : "Type to search your patients."}
                </p>
              ) : (
                candidates.map((c) => (
                  <label
                    key={c._id}
                    className={`flex cursor-pointer items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0 dark:border-slate-700 ${
                      selectedId === c._id
                        ? "bg-indigo-50 dark:bg-indigo-900/30"
                        : "hover:bg-slate-50 dark:hover:bg-slate-700/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="swap-target"
                      checked={selectedId === c._id}
                      onChange={() => setSelectedId(c._id)}
                      className="h-3.5 w-3.5 text-indigo-600"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-slate-800 dark:text-slate-200">
                        {`${c.title ? `${c.title}. ` : ""}${c.fullName}`}
                      </span>
                      <span className="block text-[11px] text-slate-500 dark:text-slate-400">
                        {c.regNo}
                        {c.mobileNo ? ` · ${c.mobileNo}` : ""}
                      </span>
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        ) : (
          <p className="mb-4 rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-600 dark:bg-slate-700/50 dark:text-slate-300">
            A real registration number is issued now, in sequence. The BMD record becomes their
            permanent patient record — complete DOB, address and other details via Edit afterwards.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded-lg bg-slate-100 px-4 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || (mode === "existing" && !selectedId)}
            className="rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-600 px-4 py-2 text-xs font-semibold text-white transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleConfirm}
          >
            {saving ? "Working…" : mode === "existing" ? "Move booking records" : "Register patient"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResolveProvisionalModal;
